/**
 * Register.tsx — rejestracja nowego tenanta (F7b T7b.6).
 *
 * Host: `app.<base>` (public scope). 3-step wizard + completion spinner + error state.
 *
 * Flow:
 *  step 1 (dane firmy)   → walidacja per-step + akceptacja regulaminu → Dalej
 *  step 2 (dane konta)   → password policy + confirm → Dalej
 *  step 3 (przegląd)     → submit → phase='submitting'
 *                          → 201 → window.location = <slug>.app.<base>/login?email=
 *                          → 4xx/5xx → phase='error' albo back-to-step z inline błędami
 *
 * Uwagi architektoniczne (D14/D15/D16 z PHASE_7B_PLAN):
 *  - D14: **nie ma progress polling** — F3 register jest synchroniczny (maxDuration=60).
 *    Response 201 przychodzi dopiero po aktywnym prowisioningu → 1 spinner wystarczy.
 *    Gdy F8 doda async job → dodamy polling GET /register/status (endpoint już jest).
 *  - D15: NIP **walidowany client-only** (10-digit + checksum). Backend zapisuje raw.
 *  - D16: po 201 **redirect cross-subdomain**, nie auto-login (refresh cookie host-scoped,
 *    nie da się ustawić na <slug>.app.<base> z public scope bez magic-linka).
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { api } from '@/lib/api';
import { buildTenantLoginUrl } from '@/lib/tenant-url';

type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';
type SlugInvalidReason = 'format' | 'length' | 'reserved';

interface CheckSlugResponse {
  available: boolean;
  reason?: string;
}

interface RegisterResponse {
  tenantId: string;
  code: string;
  slug: string;
  mode: 'shared' | 'dedicated';
  status: string;
  redirectUrl: string;
}

interface ApiErrorBody {
  error: string;
  message?: string;
  retryAfterMs?: number;
  details?: unknown;
}

interface Fields {
  companyName: string;
  taxId: string;
  ownerEmail: string;
  slug: string;
  acceptTerms: boolean;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

function emptyFields(): Fields {
  return {
    companyName: '',
    taxId: '',
    ownerEmail: '',
    slug: '',
    acceptTerms: false,
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  };
}

// Polish NIP checksum (10 digits): weights [6,5,7,2,3,4,5,6,7], sum%11 === last digit.
export function validateNipChecksum(nip: string): boolean {
  const digits = nip.replace(/[\s-]/g, '');
  if (!/^\d{10}$/.test(digits)) return false;
  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  let sum = 0;
  for (let i = 0; i < weights.length; i++) {
    sum += weights[i]! * Number(digits[i]);
  }
  const checksum = sum % 11;
  if (checksum === 10) return false;
  return checksum === Number(digits[9]);
}

function isEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

interface PolicyChecks {
  minLen: boolean;
  upper: boolean;
  digit: boolean;
}

function evaluatePolicy(password: string): PolicyChecks {
  return {
    minLen: password.length >= 10,
    upper: /[A-Z]/.test(password),
    digit: /\d/.test(password),
  };
}

export function Register() {
  const { t } = useTranslation();

  const [fields, setFields] = useState<Fields>(emptyFields());
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [phase, setPhase] = useState<'form' | 'submitting' | 'error'>('form');

  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [slugInvalidReason, setSlugInvalidReason] = useState<SlugInvalidReason | null>(null);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<'generic' | 'provisioning'>('generic');

  function setField<K extends keyof Fields>(key: K, value: Fields[K]) {
    setFields((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key as string]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key as string];
        return next;
      });
    }
  }

  // Slug debounce — mirror CreateTenantModal T7.2.
  useEffect(() => {
    const slug = fields.slug;
    if (!slug) {
      setSlugStatus('idle');
      setSlugInvalidReason(null);
      return;
    }
    if (slug.length < 3 || slug.length > 40) {
      setSlugStatus('invalid');
      setSlugInvalidReason('length');
      return;
    }

    let cancelled = false;
    setSlugStatus('checking');
    setSlugInvalidReason(null);

    const timerId = setTimeout(async () => {
      try {
        const res = await api.post<CheckSlugResponse>('/tenants/check-slug', { slug });
        if (cancelled) return;
        if (res.data.available) {
          setSlugStatus('available');
        } else {
          const reason = res.data.reason;
          if (reason === 'taken') setSlugStatus('taken');
          else {
            setSlugStatus('invalid');
            setSlugInvalidReason((reason as SlugInvalidReason) ?? 'format');
          }
        }
      } catch {
        if (cancelled) return;
        setSlugStatus('idle');
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [fields.slug]);

  function validateStep1(): boolean {
    const errors: Record<string, string> = {};
    const required = t('public.register.errors.required');
    if (!fields.companyName.trim()) errors.companyName = required;
    if (!fields.taxId.trim()) {
      errors.taxId = required;
    } else if (!validateNipChecksum(fields.taxId)) {
      errors.taxId = t('public.register.errors.taxIdInvalid');
    }
    if (!fields.ownerEmail.trim()) errors.ownerEmail = required;
    else if (!isEmail(fields.ownerEmail))
      errors.ownerEmail = t('public.register.errors.emailInvalid');
    if (!fields.slug.trim()) errors.slug = required;
    if (!fields.acceptTerms) errors.acceptTerms = t('public.register.errors.termsRequired');
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return false;
    if (slugStatus === 'taken' || slugStatus === 'invalid' || slugStatus === 'checking') {
      setFormError(t('public.register.errors.slugBlocking'));
      return false;
    }
    setFormError(null);
    return true;
  }

  function validateStep2(): boolean {
    const errors: Record<string, string> = {};
    const required = t('public.register.errors.required');
    if (!fields.firstName.trim()) errors.firstName = required;
    if (!fields.lastName.trim()) errors.lastName = required;
    const policy = evaluatePolicy(fields.password);
    if (!fields.password) errors.password = required;
    else if (!policy.minLen || !policy.upper || !policy.digit)
      errors.password = t('public.register.errors.passwordPolicy');
    if (fields.confirmPassword !== fields.password)
      errors.confirmPassword = t('public.register.errors.passwordMismatch');
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function goNext() {
    if (step === 1) {
      if (validateStep1()) {
        setStep(2);
        setFormError(null);
      }
      return;
    }
    if (step === 2) {
      if (validateStep2()) {
        setStep(3);
        setFormError(null);
      }
      return;
    }
  }

  function goBack() {
    setFormError(null);
    setFieldErrors({});
    if (step === 2) setStep(1);
    if (step === 3) setStep(2);
  }

  async function submitRegistration(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setPhase('submitting');
    try {
      const body = {
        slug: fields.slug,
        companyName: fields.companyName,
        taxId: fields.taxId || null,
        ownerEmail: fields.ownerEmail,
        ownerFirstName: fields.firstName,
        ownerLastName: fields.lastName,
        ownerPassword: fields.password,
      };
      const res = await api.post<RegisterResponse>('/tenants/register', body);
      // D16=A: cross-subdomain redirect po 201 z prefill email.
      // D24 (T7b.9): tenantId dołączany w DEV do URL — odbiorca (same-origin /login po SPA
      // reload) zapisze go do sessionStorage przez getDevTenantId(), axios interceptor użyje
      // go jako `X-Tenant-Id` dla nowo provisioned tenanta (Playwright + user-side shortcut).
      const target = buildTenantLoginUrl({
        slug: res.data.slug,
        email: fields.ownerEmail,
        tenantId: res.data.tenantId,
      });
      window.location.href = target;
      // Nie resetujemy phase — user opuszcza stronę.
    } catch (err) {
      const axErr = err as AxiosError<ApiErrorBody>;
      const status = axErr.response?.status ?? null;
      const code = axErr.response?.data?.error ?? null;
      if (status === 409 || code === 'SLUG_TAKEN') {
        setSlugStatus('taken');
        setFormError(t('public.register.errors.slugTaken'));
        setPhase('form');
        setStep(1);
      } else if (status === 422 || code === 'VALIDATION_FAILED' || code === 'INVALID_SLUG') {
        setFormError(t('public.register.errors.validation'));
        setPhase('form');
        setStep(1);
      } else if (status === 429 || code === 'RATE_LIMIT') {
        setFormError(t('public.register.errors.rateLimit'));
        setPhase('form');
        setStep(1);
      } else {
        // 500 / PROVISIONING_FAILED / network — pełen error state z kontaktem.
        setErrorKind(code === 'PROVISIONING_FAILED' ? 'provisioning' : 'generic');
        setPhase('error');
      }
    }
  }

  if (phase === 'submitting') return <SubmittingView t={t} />;
  if (phase === 'error')
    return (
      <ErrorView
        t={t}
        kind={errorKind}
        onRetry={() => {
          setPhase('form');
          setFormError(null);
        }}
      />
    );

  const policy = evaluatePolicy(fields.password);

  const slugBadge = (() => {
    if (slugStatus === 'idle') return null;
    if (slugStatus === 'checking')
      return <Badge color="gray">{t('public.register.slug.checking')}</Badge>;
    if (slugStatus === 'available')
      return <Badge color="green">{t('public.register.slug.available')}</Badge>;
    if (slugStatus === 'taken') return <Badge color="red">{t('public.register.slug.taken')}</Badge>;
    const reasonKey =
      slugInvalidReason === 'length'
        ? 'slugInvalidLength'
        : slugInvalidReason === 'reserved'
          ? 'slugInvalidReserved'
          : 'slugInvalidFormat';
    return <Badge color="amber">{t(`public.register.slug.${reasonKey}`)}</Badge>;
  })();

  const stepLabel = t(`public.register.stepLabels.${step}`);

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
      <div className="w-full max-w-xl rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary">{t('public.register.title')}</h1>
          <p className="mt-1 text-sm text-text-tertiary">{t('public.register.subtitle')}</p>
          <div
            className="mt-4 flex items-center gap-2 text-xs text-text-muted"
            aria-label={t('public.register.stepIndicatorAria')}
            data-testid="step-indicator"
          >
            <span className={step === 1 ? 'font-semibold text-text-primary' : undefined}>
              1. {t('public.register.stepLabels.1')}
            </span>
            <span aria-hidden="true">→</span>
            <span className={step === 2 ? 'font-semibold text-text-primary' : undefined}>
              2. {t('public.register.stepLabels.2')}
            </span>
            <span aria-hidden="true">→</span>
            <span className={step === 3 ? 'font-semibold text-text-primary' : undefined}>
              3. {t('public.register.stepLabels.3')}
            </span>
          </div>
          <p className="mt-2 text-sm font-medium text-text-primary">{stepLabel}</p>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (step < 3) goNext();
            else void submitRegistration(e);
          }}
          noValidate
          className="flex flex-col gap-4"
        >
          {step === 1 && (
            <>
              <Input
                label={t('public.register.fields.companyName')}
                value={fields.companyName}
                onChange={(e) => setField('companyName', e.target.value)}
                error={fieldErrors.companyName}
                required
                autoComplete="organization"
              />
              <Input
                label={t('public.register.fields.taxId')}
                value={fields.taxId}
                onChange={(e) => setField('taxId', e.target.value)}
                error={fieldErrors.taxId}
                hint={t('public.register.fields.taxIdHint')}
                required
                inputMode="numeric"
                autoComplete="off"
              />
              <Input
                label={t('public.register.fields.ownerEmail')}
                type="email"
                value={fields.ownerEmail}
                onChange={(e) => setField('ownerEmail', e.target.value)}
                error={fieldErrors.ownerEmail}
                required
                autoComplete="email"
              />
              <div className="flex flex-col gap-1">
                <Input
                  label={t('public.register.fields.slug')}
                  value={fields.slug}
                  onChange={(e) => setField('slug', e.target.value)}
                  error={fieldErrors.slug}
                  required
                  placeholder="np. acme-firma"
                  autoComplete="off"
                />
                {slugBadge && <div className="mt-0.5">{slugBadge}</div>}
                {!fieldErrors.slug && (
                  <span className="text-xs text-text-muted">
                    {t('public.register.fields.slugHint')}
                  </span>
                )}
              </div>

              <label className="flex items-start gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={fields.acceptTerms}
                  onChange={(e) => setField('acceptTerms', e.target.checked)}
                  className="mt-0.5"
                  aria-describedby={fieldErrors.acceptTerms ? 'terms-error' : undefined}
                />
                <span>
                  {t('public.register.fields.acceptTermsPrefix')}{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 underline hover:text-accent-700"
                  >
                    {t('public.register.fields.acceptTermsLink')}
                  </a>
                  .
                </span>
              </label>
              {fieldErrors.acceptTerms && (
                <span id="terms-error" role="alert" className="text-xs text-red-600">
                  {fieldErrors.acceptTerms}
                </span>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t('public.register.fields.firstName')}
                  value={fields.firstName}
                  onChange={(e) => setField('firstName', e.target.value)}
                  error={fieldErrors.firstName}
                  required
                  autoComplete="given-name"
                />
                <Input
                  label={t('public.register.fields.lastName')}
                  value={fields.lastName}
                  onChange={(e) => setField('lastName', e.target.value)}
                  error={fieldErrors.lastName}
                  required
                  autoComplete="family-name"
                />
              </div>
              <div>
                <Input
                  label={t('public.register.fields.password')}
                  type="password"
                  value={fields.password}
                  onChange={(e) => setField('password', e.target.value)}
                  error={fieldErrors.password}
                  required
                  autoComplete="new-password"
                />
                <ul
                  className="mt-2 space-y-0.5 text-xs"
                  aria-label={t('public.register.policy.heading')}
                >
                  <PolicyItem ok={policy.minLen} label={t('public.register.policy.minLen')} />
                  <PolicyItem ok={policy.upper} label={t('public.register.policy.upper')} />
                  <PolicyItem ok={policy.digit} label={t('public.register.policy.digit')} />
                </ul>
              </div>
              <Input
                label={t('public.register.fields.confirmPassword')}
                type="password"
                value={fields.confirmPassword}
                onChange={(e) => setField('confirmPassword', e.target.value)}
                error={fieldErrors.confirmPassword}
                required
                autoComplete="new-password"
              />
            </>
          )}

          {step === 3 && (
            <div
              className="flex flex-col gap-3 rounded-lg border border-border-primary bg-bg-secondary p-4 text-sm"
              data-testid="review-summary"
            >
              <h2 className="text-base font-semibold text-text-primary">
                {t('public.register.review.heading')}
              </h2>
              <ReviewRow
                label={t('public.register.review.companyLabel')}
                value={fields.companyName}
              />
              <ReviewRow label={t('public.register.review.taxIdLabel')} value={fields.taxId} />
              <ReviewRow label={t('public.register.review.emailLabel')} value={fields.ownerEmail} />
              <ReviewRow label={t('public.register.review.slugLabel')} value={fields.slug} />
              <ReviewRow
                label={t('public.register.review.userLabel')}
                value={`${fields.firstName} ${fields.lastName}`.trim()}
              />
            </div>
          )}

          {formError && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            >
              {formError}
            </div>
          )}

          <div className="mt-2 flex items-center justify-between">
            {step > 1 ? (
              <Button type="button" variant="secondary" onClick={goBack}>
                {t('public.register.nav.back')}
              </Button>
            ) : (
              <span />
            )}

            {step < 3 ? (
              <Button type="submit">{t('public.register.nav.next')}</Button>
            ) : (
              <Button type="submit" data-testid="submit-register">
                {t('public.register.nav.submit')}
              </Button>
            )}
          </div>
        </form>
      </div>
    </main>
  );
}

function PolicyItem({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li
      className={ok ? 'text-green-700 dark:text-green-300' : 'text-text-muted'}
      data-testid={`policy-${ok ? 'ok' : 'pending'}`}
    >
      <span aria-hidden="true" className="mr-1">
        {ok ? '✓' : '·'}
      </span>
      {label}
    </li>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-text-muted">{label}</span>
      <span className="text-right font-medium text-text-primary">{value || '—'}</span>
    </div>
  );
}

function SubmittingView({ t }: { t: (key: string) => string }) {
  return (
    <main
      className="flex min-h-screen items-center justify-center bg-bg-secondary p-4"
      role="status"
      aria-live="polite"
    >
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 text-center shadow-xl">
        <div
          className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-accent-200 border-t-accent-600"
          aria-hidden="true"
        />
        <h1 className="text-lg font-semibold text-text-primary">
          {t('public.register.provisioning.title')}
        </h1>
        <p className="mt-2 text-sm text-text-tertiary">
          {t('public.register.provisioning.subtitle')}
        </p>
      </div>
    </main>
  );
}

function ErrorView({
  t,
  kind,
  onRetry,
}: {
  t: (key: string) => string;
  kind: 'generic' | 'provisioning';
  onRetry: () => void;
}) {
  const titleKey =
    kind === 'provisioning'
      ? 'public.register.error.titleProvisioning'
      : 'public.register.error.title';
  const bodyKey =
    kind === 'provisioning'
      ? 'public.register.error.bodyProvisioning'
      : 'public.register.error.bodyGeneric';
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 text-center shadow-xl">
        <h1 className="text-lg font-semibold text-red-700 dark:text-red-400">{t(titleKey)}</h1>
        <p className="mt-2 text-sm text-text-tertiary">{t(bodyKey)}</p>
        <p className="mt-3 text-sm text-text-secondary">
          {t('public.register.error.contact')}{' '}
          <a
            href="mailto:support@saas.pl"
            className="text-accent-600 underline hover:text-accent-700"
          >
            support@saas.pl
          </a>
        </p>
        <div className="mt-6 flex justify-center">
          <Button onClick={onRetry}>{t('public.register.error.retry')}</Button>
        </div>
      </div>
    </main>
  );
}
